/**
 * useMigrateCheckout Hook
 *
 * Mirrors useCartCheckout pattern for bulk "Copy Listings to xrp.deals" flow.
 * Creates sell offer payloads ONE AT A TIME, showing QR code for each,
 * then advances to the next once resolved.
 */

import { useState, useCallback, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { apiClient } from '../api/client';
import { toSigningPayload } from '../lib/wallet';
import type { SigningPayload } from '../types/signing';
import { DEALS_BROKER_ADDRESS } from '../lib/marketplace-constants';

export type MigrateCheckoutStage = 'idle' | 'confirming' | 'creating' | 'complete';

export type MigrateItemStatus = 'waiting' | 'creating' | 'pending' | 'signed' | 'rejected' | 'expired' | 'error';

export interface EligibleNft {
  nft_token_id: string;
  name: string;
  thumbnail_url: string | null;
  collection_name: string | null;
  collection_slug: string | null;
  owner_address: string;
  offer: {
    price: number;
    price_drops: number;
    marketplace_name: string;
    marketplace_fee: number;
    offer_id: string;
  };
}

export interface MigrateItemState {
  nft: EligibleNft;
  payload: SigningPayload | null;
  status: MigrateItemStatus;
  error?: string;
}

export interface MigrateCheckoutState {
  stage: MigrateCheckoutStage;
  items: MigrateItemState[];
  currentIndex: number;
  currentPayload: SigningPayload | null;
  completedCount: number;
  failedCount: number;
}

const initialState: MigrateCheckoutState = {
  stage: 'idle',
  items: [],
  currentIndex: -1,
  currentPayload: null,
  completedCount: 0,
  failedCount: 0,
};

function isResolved(status: MigrateItemStatus): boolean {
  return status === 'signed' || status === 'rejected' || status === 'expired' || status === 'error';
}

function deriveCounts(items: MigrateItemState[]) {
  return {
    completedCount: items.filter((i) => i.status === 'signed').length,
    failedCount: items.filter((i) => i.status === 'rejected' || i.status === 'expired' || i.status === 'error').length,
  };
}

export function useMigrateCheckout() {
  const { user } = useAuthStore();
  const [state, setState] = useState<MigrateCheckoutState>(initialState);
  const [nftsToMigrate, setNftsToMigrate] = useState<EligibleNft[]>([]);
  const cancelledRef = useRef(false);

  const createSellPayload = useCallback(
    async (nft: EligibleNft): Promise<SigningPayload> => {
      if (!user?.xrpl_address) throw new Error('Not authenticated');

      const response = await apiClient.post<any>('/offers/create', {
        nftTokenId: nft.nft_token_id,
        offerType: 'sell',
        amount: nft.offer.price_drops,
        userAddress: nft.owner_address,
        destination: DEALS_BROKER_ADDRESS,
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        return toSigningPayload(response.data.signingIntent ?? response.data.payload);
      }
      throw new Error(response.error || 'Failed to create payload');
    },
    [user?.xrpl_address]
  );

  const startConfirmation = useCallback((nfts: EligibleNft[]) => {
    setNftsToMigrate(nfts);
    setState({ ...initialState, stage: 'confirming' });
  }, []);

  /** Create a payload for a given index and update state */
  const createPayloadForIndex = useCallback(
    async (index: number, items: MigrateItemState[]) => {
      const nft = items[index]?.nft;
      if (!nft) return;

      // Mark as creating
      setState((s) => ({
        ...s,
        currentIndex: index,
        currentPayload: null,
        items: s.items.map((ci, idx) =>
          idx === index ? { ...ci, status: 'creating' as const } : ci
        ),
      }));

      try {
        const payload = await createSellPayload(nft);
        if (cancelledRef.current) return;

        setState((s) => ({
          ...s,
          currentPayload: payload,
          items: s.items.map((ci, idx) =>
            idx === index ? { ...ci, payload, status: 'pending' as const } : ci
          ),
        }));
      } catch (err: any) {
        if (cancelledRef.current) return;

        const updatedItems = items.map((ci, idx) =>
          idx === index ? { ...ci, status: 'error' as const, error: err.message } : ci
        );
        const counts = deriveCounts(updatedItems);

        // Auto-advance to next if this one failed
        const nextIndex = index + 1;
        if (nextIndex < items.length) {
          setState((s) => ({
            ...s,
            items: updatedItems,
            ...counts,
          }));
          // Schedule next item
          setTimeout(() => createPayloadForIndex(nextIndex, updatedItems), 300);
        } else {
          setState((s) => ({
            ...s,
            stage: 'complete',
            items: updatedItems,
            currentPayload: null,
            ...counts,
          }));
        }
      }
    },
    [createSellPayload]
  );

  const startCreating = useCallback(async () => {
    if (nftsToMigrate.length === 0) return;
    cancelledRef.current = false;

    const initialItems: MigrateItemState[] = nftsToMigrate.map((nft) => ({
      nft,
      payload: null,
      status: 'waiting' as const,
    }));

    setState({
      stage: 'creating',
      items: initialItems,
      currentIndex: 0,
      currentPayload: null,
      completedCount: 0,
      failedCount: 0,
    });

    // Create first payload
    await createPayloadForIndex(0, initialItems);
  }, [nftsToMigrate, createPayloadForIndex]);

  /** Called by the modal when the current item's payload resolves */
  const resolveCurrentItem = useCallback(
    (status: 'signed' | 'rejected' | 'expired', error?: string) => {
      setState((s) => {
        const idx = s.currentIndex;
        const updatedItems = s.items.map((ci, i) =>
          i === idx ? { ...ci, status, error } : ci
        );
        const counts = deriveCounts(updatedItems);

        // Find next unresolved item
        const nextIndex = idx + 1;
        const hasMore = nextIndex < updatedItems.length && !cancelledRef.current;

        if (hasMore) {
          // Schedule next payload creation outside setState
          setTimeout(() => createPayloadForIndex(nextIndex, updatedItems), 500);
          return {
            ...s,
            items: updatedItems,
            currentPayload: null,
            ...counts,
          };
        }

        // All done
        return {
          ...s,
          stage: 'complete' as const,
          items: updatedItems,
          currentPayload: null,
          ...counts,
        };
      });
    },
    [createPayloadForIndex]
  );

  const cancelRemaining = useCallback(() => {
    cancelledRef.current = true;
    setState((s) => {
      const updated = s.items.map((i) =>
        !isResolved(i.status)
          ? { ...i, status: 'rejected' as const, error: 'Cancelled' }
          : i
      );
      const counts = deriveCounts(updated);
      return { ...s, items: updated, stage: 'complete', currentPayload: null, ...counts };
    });
  }, []);

  const reset = useCallback(() => {
    cancelledRef.current = true;
    setState(initialState);
    setNftsToMigrate([]);
  }, []);

  return {
    state,
    nftsToMigrate,
    startConfirmation,
    startCreating,
    resolveCurrentItem,
    cancelRemaining,
    reset,
  };
}
